import random from "lodash/random";
import sample from "lodash/sample";

import { BOTANIX, ASSETCHAIN, ASSETCHAIN_TESTNET, UiSupportedChain } from "./chains";

function getProcessEnv(name: string): string | undefined {
  if (typeof process === "undefined" || !(process as any).env) {
    return undefined;
  }

  const value = (process as any).env[name];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function getImportMetaEnv() {
  try {
    // `import.meta` is only available in the browser / Vite bundle, so we need to evaluate it dynamically.
    return (0, eval)("import.meta.env");
  } catch {
    return undefined;
  }
}

function getBrowserEnv(name: string): string | undefined {
  const env = getImportMetaEnv();
  const value = env?.[name];

  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function getEnv(name: string): string | undefined {
  return getProcessEnv(name) ?? getBrowserEnv(name);
}

function getEnvList(name: string, fallback: string): string[] {
  const raw = getEnv(name) ?? fallback;

  return raw
    .split(",")
    .map((url) => url.trim())
    .filter(Boolean);
}

function buildBackendKeeperUrl(envVarName: string, defaultPath: string, fallback: string): string[] {
  const explicit = getEnvList(envVarName, "");
  if (explicit.length) {
    return explicit;
  }

  const backendBase = getEnv("VITE_BACKEND_URL");
  if (backendBase) {
    return [`${backendBase.replace(/\/+$/, "")}${defaultPath}`];
  }

  return getEnvList(envVarName, fallback);
}

const ORACLE_KEEPER_URLS: Record<UiSupportedChain, string[]> = {
  [BOTANIX]: ["https://botanix-api.gmxinfra.io", "https://botanix-api.gmxinfra2.io"],

  // AssetChain production keeper – defaults to `${VITE_BACKEND_URL}/oracle-keeper` but can be
  // overridden via SDK_ORACLE_KEEPER_URLS_ASSETCHAIN (comma‑separated).
  [ASSETCHAIN]: buildBackendKeeperUrl("SDK_ORACLE_KEEPER_URLS_ASSETCHAIN", "/oracle-keeper", "http://localhost:3333/oracle-keeper"),

  // AssetChain testnet keeper – defaults to `${VITE_BACKEND_URL}/oracle-keeper-testnet` but can be
  // overridden via SDK_ORACLE_KEEPER_URLS_ASSETCHAIN_TESTNET (comma‑separated).
  [ASSETCHAIN_TESTNET]: buildBackendKeeperUrl(
    "SDK_ORACLE_KEEPER_URLS_ASSETCHAIN_TESTNET",
    "/oracle-keeper",
    "http://localhost:3333/oracle-keeper"
  ),
};

function getKeeperUrls(chainId: number) {
  return ORACLE_KEEPER_URLS[chainId as UiSupportedChain] ?? ORACLE_KEEPER_URLS[ASSETCHAIN];
}

export function getOracleKeeperUrl(chainId: number, index: number) {
  const urls = getKeeperUrls(chainId);

  if (!urls?.length) {
    // return dummy URL to avoid crashing the UI while real endpoints are wired.
    return "http://localhost:3333/oracle-keeper";
  }

  return urls[index] || urls[0];
}

export function getOracleKeeperNextIndex(chainId: number, currentIndex: number) {
  const urls = getKeeperUrls(chainId);

  if (!urls.length) {
    throw new Error(`No oracle keeper urls for chain ${chainId}`);
  }

  return urls[currentIndex + 1] ? currentIndex + 1 : 0;
}

export function getOracleKeeperRandomIndex(chainId: number, bannedIndexes?: number[]): number {
  const urls = getKeeperUrls(chainId);

  if (bannedIndexes?.length) {
    const filteredUrls = urls.filter((url, i) => !bannedIndexes.includes(i));

    if (filteredUrls.length) {
      const url = sample(filteredUrls);

      if (!url) {
        throw new Error(`No oracle keeper urls for chain ${chainId}`);
      }

      return urls.indexOf(url);
    }
  }

  return random(0, urls.length - 1);
}
